// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Search,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Users,
  RotateCcw,
  MapPin,
  CalendarDays,
} from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatCalendarDate } from "@/lib/dates";

const CYAN = '#06B6D4';

type Player = {
  roster_id: string;
  player_name: string;
  jersey_number: string | null;
  checked_in_at: string | null;
};

type Team = {
  registration_id: string;
  team_name: string;
  division: string | null;
  pool: string | null;
  payment_status: string | null;
  checked_in_at: string | null;
  checked_in_by_name: string | null;
  player_count: number;
  players_checked_in: number;
  players: Player[];
};

type SheetData = {
  event: any;
  teams: Team[];
  summary: {
    total_teams: number;
    teams_checked_in: number;
    total_players: number;
    players_checked_in: number;
  };
};

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function paymentBadgeClass(status: string | null): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-700';
    case 'comped':
      return 'bg-cyan-100 text-cyan-700';
    case 'partial':
      return 'bg-amber-100 text-amber-700';
    case 'refunded':
    case 'partially_refunded':
      return 'bg-orange-100 text-orange-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

/** Shared team check-in UI (rle-event-checkin list / checkin_team / undo_team). Used by admin and staff routes. */
const TeamCheckInPanel: React.FC<{ eventId?: string }> = ({ eventId }) => {
  const [data, setData] = useState<SheetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'not' | 'in'>('all');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const fetchData = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    const { data: res, error: err } = await invokeEdgeFunction('rle-event-checkin', {
      body: { action: 'list', event_id: eventId },
    });
    if (err || res?.error) {
      setError(res?.error || err?.message || 'Failed to load check-in data');
      setData(null);
    } else {
      setData(res?.data ?? null);
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const recompute = (teams: Team[]): SheetData['summary'] => ({
    total_teams: teams.length,
    teams_checked_in: teams.filter((t) => t.checked_in_at).length,
    total_players: teams.reduce((s, t) => s + t.player_count, 0),
    players_checked_in: teams.reduce(
      (s, t) => s + t.players.filter((p) => p.checked_in_at).length,
      0,
    ),
  });

  const setTeams = (mut: (teams: Team[]) => Team[]) => {
    setData((prev) => {
      if (!prev) return prev;
      const teams = mut(prev.teams.map((t) => ({ ...t, players: [...t.players] })));
      return { ...prev, teams, summary: recompute(teams) };
    });
  };

  const checkInTeam = async (team: Team, undo: boolean) => {
    const id = team.registration_id;
    if (busy[`t-${id}`]) return;
    setBusy((b) => ({ ...b, [`t-${id}`]: true }));
    const now = new Date().toISOString();
    // optimistic
    setTeams((teams) =>
      teams.map((t) =>
        t.registration_id === id ? { ...t, checked_in_at: undo ? null : now } : t,
      ),
    );
    const { data: res, error: err } = await invokeEdgeFunction('rle-event-checkin', {
      body: { action: undo ? 'undo_team' : 'checkin_team', registration_id: id },
    });
    if (err || res?.error || res?.ok === false) {
      // revert
      setTeams((teams) =>
        teams.map((t) =>
          t.registration_id === id ? { ...t, checked_in_at: team.checked_in_at } : t,
        ),
      );
      toast.error(res?.error || err?.message || 'Check-in failed');
    } else {
      const serverTime = res?.data?.checked_in_at ?? (undo ? null : now);
      setTeams((teams) =>
        teams.map((t) =>
          t.registration_id === id ? { ...t, checked_in_at: serverTime } : t,
        ),
      );
    }
    setBusy((b) => ({ ...b, [`t-${id}`]: false }));
  };

  const checkInPlayer = async (teamId: string, player: Player, undo: boolean) => {
    const id = player.roster_id;
    if (busy[`p-${id}`]) return;
    setBusy((b) => ({ ...b, [`p-${id}`]: true }));
    const now = new Date().toISOString();
    setTeams((teams) =>
      teams.map((t) =>
        t.registration_id !== teamId
          ? t
          : {
              ...t,
              players: t.players.map((p) =>
                p.roster_id === id ? { ...p, checked_in_at: undo ? null : now } : p,
              ),
            },
      ),
    );
    const { data: res, error: err } = await invokeEdgeFunction('rle-event-checkin', {
      body: { action: undo ? 'undo_player' : 'checkin_player', roster_id: id },
    });
    if (err || res?.error || res?.ok === false) {
      setTeams((teams) =>
        teams.map((t) =>
          t.registration_id !== teamId
            ? t
            : {
                ...t,
                players: t.players.map((p) =>
                  p.roster_id === id ? { ...p, checked_in_at: player.checked_in_at } : p,
                ),
              },
        ),
      );
      toast.error(res?.error || err?.message || 'Check-in failed');
    } else {
      const serverTime = res?.data?.checked_in_at ?? (undo ? null : now);
      setTeams((teams) =>
        teams.map((t) =>
          t.registration_id !== teamId
            ? t
            : {
                ...t,
                players: t.players.map((p) =>
                  p.roster_id === id ? { ...p, checked_in_at: serverTime } : p,
                ),
              },
        ),
      );
    }
    setBusy((b) => ({ ...b, [`p-${id}`]: false }));
  };

  const filteredTeams = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.teams.filter((t) => {
      if (q && !t.team_name?.toLowerCase().includes(q)) return false;
      if (filter === 'in' && !t.checked_in_at) return false;
      if (filter === 'not' && t.checked_in_at) return false;
      return true;
    });
  }, [data, search, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading check-in…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <p className="text-lg font-semibold text-slate-800">Unable to load check-in</p>
        <p className="text-sm text-slate-500 mt-2">{error || 'No data available.'}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 rounded-md text-white text-sm font-medium"
          style={{ background: CYAN }}
        >
          Retry
        </button>
      </div>
    );
  }

  const { event, summary } = data;
  const eventName = event?.name ?? 'Event';
  const venue = event?.venue_name ?? event?.location ?? event?.venue ?? '';
  const dates = [event?.start_date, event?.end_date]
    .filter(Boolean)
    .map((d: string) => formatCalendarDate(d, 'MMM d', ''))
    .filter(Boolean)
    .join(' – ');

  return (
    <div className="min-h-screen w-full bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F172A] text-white px-5 py-5">
        <h1 className="text-2xl font-bold">{eventName}</h1>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-sm text-slate-300">
          {venue && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-4 h-4" /> {venue}
            </span>
          )}
          {dates && (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="w-4 h-4" /> {dates}
            </span>
          )}
        </div>
        {(event?.checkin_short_description || event?.checkin_instructions) && (
          <p className="mt-3 text-sm text-slate-200 max-w-3xl whitespace-pre-line">
            {event?.checkin_instructions || event?.checkin_short_description}
          </p>
        )}
      </div>

      {/* Sticky SummaryBar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-5 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm font-medium text-slate-700">
            <span className="text-slate-500">Teams:</span>{' '}
            <span style={{ color: CYAN }} className="font-bold">
              {summary.teams_checked_in}/{summary.total_teams}
            </span>{' '}
            checked in
          </div>
          <div className="text-sm font-medium text-slate-700">
            <span className="text-slate-500">Players:</span>{' '}
            <span style={{ color: CYAN }} className="font-bold">
              {summary.players_checked_in}/{summary.total_players}
            </span>
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teams…"
              className="pl-9 h-10"
            />
          </div>
          <div className="flex rounded-md overflow-hidden border border-slate-200">
            {([
              ['all', 'All'],
              ['not', 'Not checked in'],
              ['in', 'Checked in'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className="px-3 h-10 text-sm font-medium transition-colors"
                style={
                  filter === key
                    ? { background: CYAN, color: 'white' }
                    : { background: 'white', color: '#475569' }
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Team list */}
      <div className="px-3 sm:px-5 py-5 space-y-3 w-full">
        {filteredTeams.length === 0 && (
          <p className="text-center text-slate-400 py-10">No teams match.</p>
        )}
        {filteredTeams.map((team) => {
          const isIn = !!team.checked_in_at;
          const open = !!expanded[team.registration_id];
          const tBusy = !!busy[`t-${team.registration_id}`];
          const playersIn = team.players.filter((p) => p.checked_in_at).length;
          return (
            <div
              key={team.registration_id}
              className={`rounded-lg border bg-white overflow-hidden transition-colors ${
                isIn ? 'border-green-300' : 'border-slate-200'
              }`}
            >
              <div className={`flex items-center gap-3 p-4 ${isIn ? 'bg-green-50' : ''}`}>
                <button
                  onClick={() =>
                    setExpanded((e) => ({
                      ...e,
                      [team.registration_id]: !e[team.registration_id],
                    }))
                  }
                  className="text-slate-400 hover:text-slate-600 shrink-0"
                  aria-label="Expand"
                >
                  {open ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 truncate">
                      {team.team_name}
                    </span>
                    {team.payment_status && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${paymentBadgeClass(
                          team.payment_status,
                        )}`}
                      >
                        {team.payment_status}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {team.division && <span>Div {team.division}</span>}
                    {team.pool && <span>Pool {team.pool}</span>}
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {playersIn}/{team.player_count} players
                    </span>
                  </div>
                </div>

                {isIn ? (
                  <div className="flex flex-col items-end shrink-0">
                    <span className="inline-flex items-center gap-1 text-green-700 font-semibold text-sm">
                      <CheckCircle2 className="w-5 h-5" /> Checked in {fmtTime(team.checked_in_at)}
                    </span>
                    <button
                      onClick={() => checkInTeam(team, true)}
                      disabled={tBusy}
                      className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mt-1"
                    >
                      {tBusy ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3" />
                      )}{' '}
                      Undo
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => checkInTeam(team, false)}
                    disabled={tBusy}
                    className="shrink-0 px-5 h-12 rounded-lg text-white font-semibold text-base inline-flex items-center gap-2 disabled:opacity-60"
                    style={{ background: CYAN }}
                  >
                    {tBusy ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5" />
                    )}
                    Check In
                  </button>
                )}
              </div>

              {open && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {team.players.length === 0 && (
                    <p className="px-4 py-3 text-sm text-slate-400">No players on roster.</p>
                  )}
                  {team.players.map((p) => {
                    const pIn = !!p.checked_in_at;
                    const pBusy = !!busy[`p-${p.roster_id}`];
                    return (
                      <button
                        key={p.roster_id}
                        onClick={() => checkInPlayer(team.registration_id, p, pIn)}
                        disabled={pBusy}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-60"
                      >
                        <span className="shrink-0">
                          {pBusy ? (
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                          ) : pIn ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-300" />
                          )}
                        </span>
                        <span className="flex-1 text-sm text-slate-700">
                          {p.jersey_number && (
                            <span className="font-mono text-slate-400 mr-2">
                              #{p.jersey_number}
                            </span>
                          )}
                          {p.player_name}
                        </span>
                        {pIn && (
                          <span className="text-xs text-green-600">
                            {fmtTime(p.checked_in_at)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamCheckInPanel;
